// Omise Token Creation Service
import config from '../../config'

// Required card data interface
export interface OmiseCardData {
  name: string // Required - Cardholder name
  number: string // Required - Card number
  expiration_month: number // Required - Expiration month (1-12)
  expiration_year: number // Required - Expiration year (YYYY)
  city?: string // Optional - City
  postal_code?: string // Optional - Postal code
  security_code: string // Optional - CVV/CVC
}

// Card object from Omise response (updated to match actual response)
export interface OmiseCard {
  object: 'card'
  id: string
  livemode: boolean
  security_code_check: boolean
  expiration_month: number
  expiration_year: number
  bank: string
  brand: string
  city: string | null
  country: string
  financing: string
  fingerprint: string
  first_digits: string | null
  last_digits: string
  name: string
  phone_number: string | null
  postal_code: string | null
  state: string | null
  street1: string | null
  street2: string | null
  tokenization_method: string | null
  created: string
}

// Token response from Omise (updated to match actual response)
export interface OmiseTokenResponse {
  object: 'token'
  id: string
  livemode: boolean
  used: boolean
  card: OmiseCard
  location: string
  charge_status: 'unknown' | 'pending' | 'successful' | 'failed'
  created: string
}

// Error response from Omise
export interface OmiseErrorResponse {
  object: 'error'
  location: string
  type: string
  code: string
  message: string
}

// Service response wrapper
export interface OmiseServiceResponse<T> {
  success: boolean
  data?: T
  error?: OmiseErrorResponse
}

class OmiseService {
  private readonly vaultUrl = 'https://vault.omise.co'

  //Create a token for a card
  async createToken(cardData: OmiseCardData): Promise<OmiseServiceResponse<OmiseTokenResponse>> {
    try {
      // Validate required fields
      this.validateCardData(cardData)

      // Prepare URL-encoded form parameters (like curl -d)
      const params = new URLSearchParams()

      // Required fields
      params.append('card[name]', cardData.name)
      params.append('card[number]', cardData.number)
      params.append('card[expiration_month]', cardData.expiration_month.toString())
      params.append('card[expiration_year]', cardData.expiration_year.toString())
      params.append('card[security_code]', cardData.security_code)

      // Optional fields
      if (cardData.city) {
        params.append('card[city]', cardData.city)
      }
      if (cardData.postal_code) {
        params.append('card[postal_code]', cardData.postal_code)
      }
      // if (cardData.security_code) {
      //   params.append('card[security_code]', cardData.security_code)
      // }

      // Create authorization header (Basic auth with public key)
      const auth: string = config.omiseService.PUBLIC_KEY || ''

      const response = await fetch(`${this.vaultUrl}/tokens`, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${auth}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      })

      const responseData = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: responseData as OmiseErrorResponse,
        }
      }

      return {
        success: true,
        data: responseData as OmiseTokenResponse,
      }
    } catch (error) {
      return {
        success: false,
        error: {
          object: 'error',
          location: '',
          type: 'network_error',
          code: 'network_error',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
        },
      }
    }
  }

  /**
   * Validate required card data fields  /**
   * Validate required card data fields
   * @param cardData - Card information to validate
   */
  private validateCardData(cardData: OmiseCardData): void {
    const errors: string[] = []

    // Required field validation
    if (!cardData.name || cardData.name.trim() === '') {
      errors.push('Card holder name is required')
    }

    if (!cardData.number || cardData.number.trim() === '') {
      errors.push('Card number is required')
    }

    if (
      !cardData.expiration_month ||
      cardData.expiration_month < 1 ||
      cardData.expiration_month > 12
    ) {
      errors.push('Valid expiration month (1-12) is required')
    }

    if (!cardData.expiration_year || cardData.expiration_year < new Date().getFullYear()) {
      errors.push('Valid expiration year is required')
    }

    // Basic card number format validation (remove spaces and check if it's numeric)
    const cleanCardNumber = cardData.number.replace(/\s/g, '')
    if (!/^\d{13,19}$/.test(cleanCardNumber)) {
      errors.push('Card number must be between 13-19 digits')
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }
  }

  /**
   * Get just the token ID from card data (simplified method)
   * @param cardData - Card information
   * @returns Promise<string | null> - Returns token ID or null if failed
   */
  async getTokenId(cardData: OmiseCardData): Promise<string | null> {
    try {
      const result = await this.createToken(cardData)

      if (result.success && result.data) {
        return result.data.id // This will be like "tokn_test_654r8sxznzueb7e1ns3"
      }

      return null
    } catch (error) {
      return null
    }
  }

  /**
   * Create token and return both token ID and card info
   * @param cardData - Card information
   * @returns Promise<{tokenId: string, cardInfo: {brand: string, lastDigits: string}} | null>
   */
  async createTokenWithCardInfo(cardData: OmiseCardData): Promise<{
    tokenId: string
    cardInfo: {
      brand: string
      lastDigits: string
      expirationMonth: number
      expirationYear: number
    }
  } | null> {
    try {
      const result = await this.createToken(cardData)

      if (result.success && result.data) {
        return {
          tokenId: result.data.id,
          cardInfo: {
            brand: result.data.card.brand,
            lastDigits: result.data.card.last_digits,
            expirationMonth: result.data.card.expiration_month,
            expirationYear: result.data.card.expiration_year,
          },
        }
      }

      return null
    } catch (error) {
      return null
    }
  }
}

// Export singleton instance
const omiseService = new OmiseService()
export default omiseService

// Named exports for convenience
export { omiseService }
